import { useEffect, useState } from "react";
import Header from "@/components/Header";
import { api, formatApiError } from "@/lib/api";
import { Target, BarChart3, Award, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  } catch { return "—"; }
}

export default function RankStatus() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/rank-status");
        setData(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "#560d0d" }}>
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 pb-16 space-y-6 text-white">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}>
            Rank Progression &amp; History
          </h1>
          <p className="text-white/70 mt-1 text-sm">
            Track your current rank and view your achievement history
          </p>
        </div>

        {loading || !data ? (
          <div className="py-20 flex justify-center">
            <div className="h-8 w-8 rounded-full border-4 border-white/40 border-t-white animate-spin" />
          </div>
        ) : (
          <>
            {/* Top cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div
                data-testid="current-rank-card"
                className="rounded-xl p-4 bg-gradient-to-br from-sky-50 to-sky-100 text-slate-900 shadow-md"
              >
                <div className="text-xs uppercase tracking-widest text-blue-800 font-bold">
                  Current Rank
                </div>
                <div className="mt-1 text-3xl font-extrabold text-blue-900"
                     style={{ fontFamily: "Outfit, sans-serif" }}>
                  {data.current_rank}
                </div>
              </div>
              <div
                data-testid="user-id-card"
                className="rounded-xl p-4 bg-gradient-to-br from-fuchsia-50 to-purple-100 text-slate-900 shadow-md"
              >
                <div className="text-xs uppercase tracking-widest text-purple-800 font-bold">
                  User ID
                </div>
                <div className="mt-1 text-3xl font-extrabold text-purple-900"
                     style={{ fontFamily: "Outfit, sans-serif" }}>
                  {data.user.user_code}
                </div>
              </div>
            </div>

            {/* Rank Progression System */}
            <section className="rounded-2xl bg-white text-slate-900 overflow-hidden shadow-lg">
              <div className="p-5 bg-slate-900 text-white">
                <h2 className="text-lg font-extrabold"
                    style={{ fontFamily: "Outfit, sans-serif" }}>
                  Rank Progression System
                </h2>
                <p className="text-xs text-white/60 mt-0.5">
                  Reference guide for all rank levels and requirements
                </p>
              </div>
              <div className="overflow-x-auto">
                <table data-testid="rank-ladder-table" className="w-full text-sm">
                  <thead className="bg-slate-100 text-[11px] uppercase tracking-widest text-slate-500 font-bold">
                    <tr>
                      <th className="text-left px-5 py-3 w-16">Level</th>
                      <th className="text-left px-5 py-3">Target Team</th>
                      <th className="text-left px-5 py-3">Rank</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.ladder.map((row) => {
                      const active = row.rank === data.current_rank;
                      return (
                        <tr
                          key={row.level}
                          data-testid={`rank-ladder-row-${row.level}`}
                          className={`border-t border-slate-100 ${
                            active ? "bg-sky-50" : "hover:bg-slate-50"
                          }`}
                        >
                          <td className="px-5 py-3 font-mono text-slate-500">{row.level}</td>
                          <td className="px-5 py-3 font-semibold text-slate-800">{row.target}</td>
                          <td className={`px-5 py-3 font-extrabold uppercase ${active ? "text-sky-800" : "text-slate-900"}`}>
                            {row.rank}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-5 py-3 bg-amber-50 border-t border-amber-200 text-xs text-amber-900">
                <b>Note:</b> Rank upgrades automatically on achievement of target team condition.
              </div>
            </section>

            {/* Rank Achievement History */}
            <section className="rounded-2xl bg-white text-slate-900 overflow-hidden shadow-lg">
              <div className="p-5 bg-slate-900 text-white">
                <h2 className="text-lg font-extrabold"
                    style={{ fontFamily: "Outfit, sans-serif" }}>
                  Your Rank Achievement History
                </h2>
                <p className="text-xs text-white/60 mt-0.5">
                  Track your rank milestones and achievement dates
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 text-[11px] uppercase tracking-widest text-slate-500 font-bold">
                    <tr>
                      <th className="text-left px-5 py-3 w-20">S.No.</th>
                      <th className="text-left px-5 py-3">Ranks</th>
                      <th className="text-left px-5 py-3">Achievement Date</th>
                    </tr>
                  </thead>
                  <tbody data-testid="rank-history-table">
                    {data.history.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="px-5 py-6 text-center text-slate-500">
                          No rank milestones yet
                        </td>
                      </tr>
                    ) : (
                      data.history.map((h, i) => (
                        <tr key={`${h.rank}-${h.at || i}`} className="border-t border-slate-100">
                          <td className="px-5 py-3 font-mono text-slate-500">{i + 1}</td>
                          <td className="px-5 py-3 font-extrabold text-slate-900 uppercase">{h.rank}</td>
                          <td className="px-5 py-3 text-slate-700">{formatDate(h.at)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            {/* How it works + Your Progress */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <section className="rounded-2xl bg-sky-50 text-slate-900 p-5 shadow-lg">
                <h3 className="font-extrabold text-slate-900 text-lg flex items-center gap-2"
                    style={{ fontFamily: "Outfit, sans-serif" }}>
                  <Target size={18} className="text-red-600" />
                  How Rank Progression Works
                </h3>
                <ol className="mt-3 space-y-2 text-sm list-decimal list-inside marker:text-slate-500">
                  <li>You start as <b>MEMBER</b> when you sign up</li>
                  <li>Complete topup activation to become <b>EXECUTIVE</b></li>
                  <li>Build a qualified downline team to meet rank requirements and unlock higher ranks</li>
                  <li>The highest rank you can achieve is <b>DIRECTOR</b> at Level 8</li>
                </ol>
              </section>

              <section className="rounded-2xl bg-emerald-50 text-emerald-900 p-5 shadow-lg">
                <h3 className="font-extrabold text-slate-900 text-lg flex items-center gap-2"
                    style={{ fontFamily: "Outfit, sans-serif" }}>
                  <BarChart3 size={18} className="text-emerald-600" />
                  Your Progress
                </h3>
                <div className="mt-3 space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span>Total Ranks Achieved</span>
                    <span data-testid="progress-achieved" className="font-extrabold text-emerald-800 text-lg">
                      {data.history.length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Current Rank Level</span>
                    <span data-testid="progress-level" className="font-extrabold text-emerald-800 text-lg">
                      {data.current_rank_index} / {data.total_ranks - 1}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Next Milestone</span>
                    <span data-testid="progress-next" className="font-extrabold text-emerald-800 uppercase">
                      {data.next_progress?.next_rank ||
                        (data.current_rank_index < data.total_ranks - 1
                          ? data.ladder[data.current_rank_index + 1]?.rank
                          : "Max rank reached")}
                    </span>
                  </div>
                  {data.next_progress && (
                    <div className="pt-2 border-t border-emerald-200">
                      <div className="text-xs text-emerald-700 mb-1">
                        {data.next_progress.requirement} — {data.next_progress.have}/{data.next_progress.need}
                      </div>
                      <div className="h-2 rounded-full bg-emerald-100 overflow-hidden">
                        <div
                          data-testid="progress-bar"
                          className="h-full bg-emerald-500 transition-all"
                          style={{
                            width: `${Math.min(
                              100,
                              (data.next_progress.have / data.next_progress.need) * 100
                            )}%`,
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </section>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
